import type { FlightDto } from '@flightlog/shared';
import { formatDistance, formatDuration, localParts } from '../format';
import { ChevronDownIcon, PlaneIcon, TrashIcon } from '../icons';
import { useState } from 'react';

interface Props {
  flight: FlightDto;
  onClose: () => void;
  onDelete: (id: string) => Promise<void>;
}

const STATUS_LABEL: Record<string, string> = {
  scheduled: 'по расписанию',
  delayed: 'задержан',
  boarding: 'посадка',
  departed: 'в воздухе',
  landed: 'совершён',
  cancelled: 'отменён',
  unknown: 'нет данных',
};

export function FlightDetail({ flight, onClose, onDelete }: Props) {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);

  const dep = localParts(flight.scheduledDepUtc, flight.dep.tz);
  const arr = localParts(flight.scheduledArrUtc, flight.arr.tz);
  const sameDay = dep.date === arr.date;
  const approx = flight.timesEstimated ? '≈ ' : '';
  // Статус «по расписанию» сам не меняется после вылета.
  const status =
    flight.status === 'scheduled' && new Date(flight.scheduledDepUtc).getTime() <= Date.now()
      ? 'landed'
      : flight.status;

  const longDate = new Intl.DateTimeFormat('ru-RU', {
    timeZone: flight.dep.tz,
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    weekday: 'short',
  }).format(new Date(flight.scheduledDepUtc));

  async function handleDelete() {
    if (!confirming) {
      setConfirming(true);
      return;
    }
    setBusy(true);
    try {
      await onDelete(flight.id);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="overlay" role="dialog" aria-modal="true">
      <button type="button" className="overlay-backdrop" aria-label="Закрыть" onClick={onClose} />
      <div className="sheet">
        <div className="sheet-grab" />
        <div className="sheet-top">
          <span className="eyebrow">Рейс {flight.flightNumber}</span>
          <button type="button" className="icon-button" onClick={onClose} aria-label="Закрыть">
            <ChevronDownIcon />
          </button>
        </div>

        <div className="detail-route">
          <div className="detail-airport">
            <b>{flight.dep.iata}</b>
            <span>{flight.dep.city}</span>
          </div>
          <div className="detail-line">
            <PlaneIcon size={18} />
            <em>
              {approx}
              {formatDuration(flight.durationMin)}
            </em>
          </div>
          <div className="detail-airport detail-airport-right">
            <b>{flight.arr.iata}</b>
            <span>{flight.arr.city}</span>
          </div>
        </div>

        <p className="detail-date">
          {longDate} · {flight.airline?.name ?? 'Авиакомпания'}
        </p>

        <div className="timeline">
          <div className="timeline-row">
            <div className="timeline-time">
              <b>{dep.time}</b>
              <span>{flight.dep.tz.split('/')[1]?.replace('_', ' ')}</span>
            </div>
            <div className="timeline-body">
              <strong>Вылет</strong>
              <span>
                {flight.dep.name}, {flight.dep.city}
              </span>
            </div>
          </div>

          <div className="timeline-divider" />

          <div className="timeline-row">
            <div className="timeline-time">
              <b>
                {approx}
                {arr.time}
              </b>
              <span>{sameDay ? flight.arr.tz.split('/')[1]?.replace('_', ' ') : `+1 · ${arr.day} ${arr.month}`}</span>
            </div>
            <div className="timeline-body">
              <strong>Прилёт</strong>
              <span>
                {flight.arr.name}, {flight.arr.city}
              </span>
            </div>
          </div>
        </div>

        {flight.timesEstimated ? (
          <p className="detail-note">
            ≈ Времени прилёта в источнике нет — оно оценено по
            расстоянию.
          </p>
        ) : null}

        <div className="facts">
          <div className="fact">
            <span>Расстояние</span>
            <b>{formatDistance(flight.distanceKm)}</b>
          </div>
          <div className="fact">
            <span>В воздухе</span>
            <b>
              {approx}
              {formatDuration(flight.durationMin)}
            </b>
          </div>
          <div className="fact">
            <span>Борт</span>
            <b>{flight.aircraft ?? '—'}</b>
          </div>
          <div className="fact">
            <span>Место</span>
            <b>{flight.seat ?? '—'}</b>
          </div>
          <div className="fact">
            <span>Статус</span>
            <b>{STATUS_LABEL[status] ?? status}</b>
          </div>
          <div className="fact">
            <span>Источник</span>
            <b>{flight.source === 'manual' ? 'вручную' : 'импорт'}</b>
          </div>
        </div>

        <button
          type="button"
          className={`button button-ghost button-danger ${confirming ? 'is-confirming' : ''}`}
          onClick={handleDelete}
          disabled={busy}
        >
          <TrashIcon />
          {confirming ? 'Точно удалить?' : 'Удалить рейс'}
        </button>
      </div>
    </div>
  );
}
